import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { initials } from "@/lib/format";
import { isAdmin } from "@/lib/permissions";
import { isNotifTypeEnabled } from "@/lib/preferences";
import { GlobalSearch } from "@/components/feature/GlobalSearch";
import { QuickCreate } from "@/components/feature/QuickCreate";

interface NotifRow {
  id: string;
  type: string;
  title: string;
  message: string | null;
  is_read: boolean;
  created_at: string;
}

export function TopBar() {
  const { user, profile, org, orgs, role, switchOrg, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const orgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) setOrgMenuOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setNotifOpen(false);
        setUserOpen(false);
        setOrgMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications", org?.id, user?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("notifications")
        .select("id, type, title, message, is_read, created_at")
        .eq("org_id", org!.id)
        .order("created_at", { ascending: false })
        .limit(20);
      return (data ?? []) as NotifRow[];
    },
  });

  // Refresh operational notifications (low stock, overdue POs, returns, ready-to-ship) on load
  useEffect(() => {
    if (!org?.id) return;
    let cancelled = false;
    void (async () => {
      try {
        await supabase.rpc("refresh_operational_notifications", { p_org_id: org.id });
        if (!cancelled) queryClient.invalidateQueries({ queryKey: ["notifications", org.id, user?.id] });
      } catch {
        // Notification refresh is best-effort only.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.id, user?.id, queryClient]);

  const visibleNotifications = notifications.filter((n) => isNotifTypeEnabled(n.type));
  const unreadCount = visibleNotifications.filter((n) => !n.is_read).length;

  const markRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["notifications", org?.id, user?.id] });
  };

  const fullName = profile?.full_name || user?.email?.split("@")[0] || "User";
  const email = user?.email ?? "";
  const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Member";

  const handleSignOut = async () => {
    setUserOpen(false);
    await signOut();
    navigate("/login");
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 sticky top-0 z-30">
      {/* Home button */}
      <button
        onClick={() => navigate("/dashboard")}
        aria-label="Go to dashboard"
        title="Dashboard"
        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 transition-colors mr-1"
      >
        <i className="ri-home-5-line" aria-hidden="true" />
      </button>

      {/* Org selector */}
      <div className="relative" ref={orgRef}>
        <button
          onClick={() => setOrgMenuOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={orgMenuOpen}
          aria-label="Switch organization"
          className="flex items-center gap-2 hover:bg-slate-100 rounded-lg px-2 py-1.5 transition-colors"
        >
          <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center">
            <i className="ri-building-line text-slate-500 text-sm" aria-hidden="true" />
          </div>
          <span className="text-sm font-medium text-slate-700 hidden sm:block">{org?.name ?? "Organization"}</span>
          {orgs.length > 1 && <i className="ri-arrow-down-s-line text-slate-400 text-xs hidden sm:block" aria-hidden="true" />}
        </button>
        {orgMenuOpen && orgs.length > 1 && (
          <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50" role="menu" aria-label="Switch organization">
            <p className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Switch organization</p>
            {orgs.map(({ org: o, role: r }) => (
              <button
                key={o.id}
                onClick={() => { setOrgMenuOpen(false); if (o.id !== org?.id) switchOrg(o.id); }}
                role="menuitem"
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors flex items-center justify-between gap-2 ${
                  o.id === org?.id ? "text-emerald-700 font-medium" : "text-slate-700"
                }`}
              >
                <span>{o.name}</span>
                <span className="text-xs text-slate-400 capitalize">{r}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1" />

      <div className="hidden md:block mr-4">
        <GlobalSearch />
      </div>

      <QuickCreate />

      <div className="relative mr-3" ref={notifRef}>
        <button
          onClick={() => setNotifOpen(!notifOpen)}
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
          aria-haspopup="true"
          aria-expanded={notifOpen}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 relative"
        >
          <i className="ri-notification-3-line text-slate-600" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center" aria-hidden="true">
              {unreadCount}
            </span>
          )}
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50" role="menu" aria-label="Notifications">
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">Notifications</span>
              <span className="text-xs text-slate-500">{unreadCount} unread</span>
            </div>
            {visibleNotifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">No notifications</div>
            ) : (
              visibleNotifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  role="menuitem"
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${
                    !n.is_read ? "bg-slate-50/50" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        n.type === "warning" ? "bg-amber-500" :
                        n.type === "error" ? "bg-red-500" :
                        n.type === "success" ? "bg-emerald-500" :
                        "bg-slate-400"
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
            <button
              onClick={() => {
                setNotifOpen(false);
                navigate("/notifications");
              }}
              className="w-full px-4 py-2.5 text-center text-xs font-medium text-emerald-600 hover:bg-emerald-50 border-t border-slate-100"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>

      <div className="relative" ref={userRef}>
        <button
          onClick={() => setUserOpen(!userOpen)}
          aria-label={`Account menu for ${fullName}`}
          aria-haspopup="true"
          aria-expanded={userOpen}
          className="flex items-center gap-2.5 hover:bg-slate-100 rounded-lg px-2 py-1.5 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm" aria-hidden="true">
            {initials(fullName)}
          </div>
          <div className="hidden lg:block text-left">
            <p className="text-sm font-medium text-slate-800 leading-none">{fullName}</p>
            <p className="text-xs text-slate-500 mt-0.5">{roleLabel}</p>
          </div>
          <i className="ri-arrow-down-s-line text-slate-400 hidden lg:block" aria-hidden="true" />
        </button>

        {userOpen && (
          <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50" role="menu" aria-label="Account">
            <div className="px-4 py-2 border-b border-slate-100">
              <p className="text-sm font-medium text-slate-800">{fullName}</p>
              <p className="text-xs text-slate-500">{email}</p>
            </div>
            <button
              onClick={() => { setUserOpen(false); navigate("/settings/profile"); }}
              role="menuitem"
              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <i className="ri-user-3-line text-slate-400" aria-hidden="true" />
              My Profile
            </button>
            <button
              onClick={() => { setUserOpen(false); navigate("/settings/preferences"); }}
              role="menuitem"
              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <i className="ri-settings-3-line text-slate-400" aria-hidden="true" />
              Preferences
            </button>
            {isAdmin(role) && (
              <button
                onClick={() => { setUserOpen(false); navigate("/settings/organization"); }}
                role="menuitem"
                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              >
                <i className="ri-building-line text-slate-400" aria-hidden="true" />
                Organization Settings
              </button>
            )}
            <div className="border-t border-slate-100 mt-1" />
            <button
              onClick={handleSignOut}
              role="menuitem"
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <i className="ri-logout-box-line text-red-400" aria-hidden="true" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
